import { Prerender, Meta, Breadcrumbs, createHandle, createLoader, layout } from "@rangojs/router";
import { Link } from "@rangojs/router/client";
import { ProductPrice } from "../../components/ProductPrice.js";
import { Pagination, PaginationLayout } from "./pagination.js";
import { createBuildCommerceClient } from "../../commerce/build-client.js";
import { loader } from "@rangojs/router";

export const ProductsHandle = createHandle<string[], string[]>((segments) => segments.flat(3));

export interface PriceData {
  [productId: string]: { price: number; wasPrice?: number; currency: string };
}

export const DummyLoader = createLoader(async (_ctx) => {
  return { dummy: "data" };
});

function buildPriceData(products: { data?: any[] }): PriceData {
  const result: PriceData = {};
  for (const product of products.data ?? []) {
    if (product.price != null) {
      const pid = product.id;
      const hasDiscount = pid.includes("5") || pid.includes("3");
      result[pid] = {
        price: hasDiscount ? product.price * 0.85 : product.price,
        ...(hasDiscount ? { wasPrice: product.price } : {}),
        currency: product.currency ?? "GBP",
      };
    }
    for (const variant of product.variants ?? []) {
      if (variant.price != null) {
        const vid = variant.productId;
        const hasDiscount = vid.includes("5") || vid.includes("3");
        result[vid] = {
          price: hasDiscount ? variant.price * 0.85 : variant.price,
          ...(hasDiscount ? { wasPrice: variant.price } : {}),
          currency: product.currency ?? "GBP",
        };
      }
    }
  }
  return result;
}

// PPR shape: the container `{ prices }` settles immediately so the bake lane
// records it without gating capture on render completion. Anonymous visitors
// get no fetch at all — base prices render from the (prerendered) product data
// and the pinned `{}` is correct for them. Basket pricing fetches per request;
// its liveness on shell HITs is physics (pending at the capture window), not a
// guarantee — a fast settle pins the CAPTURING user's map for the shell's TTL
// (accepted: treat bake-lane fast-resolvers as pinned).
export const PriceLoader = createLoader(async (ctx) => {
  "use server";
  const t0 = performance.now();
  const prices: Promise<PriceData> = (async () => {
    await ctx.rendered();
    const productIds = ctx.use(ProductsHandle);
    if (!productIds.length) return {} as PriceData;

    const hasBasket = !!ctx.get("session")?.get("basketId");
    if (!hasBasket) return {} as PriceData;
    try {
      const products = await ctx.get("userCommerce")!.shopperProducts.getProducts({
        parameters: { ids: productIds, expand: ["prices", "variations"] },
      });
      return buildPriceData(products);
    } catch (e) {
      console.error(`[price] getProducts failed:`, e);
      return {} as PriceData;
    } finally {
      console.log(`[price] ${productIds.length} ids: ${(performance.now() - t0).toFixed(1)}ms`);
    }
  })();
  // Swallow rejections on the container copy; consumers handle their own use()
  prices.catch(() => {});

  return { prices };
});

const ITEMS_PER_PAGE = 20;
export const CategoryPage = Prerender<{ categoryId: string; page?: string }>(
  async () => {
    const commerce = await createBuildCommerceClient();
    const root = await commerce.shopperProducts.getCategory({
      parameters: {
        id: "root",
        levels: 3,
        currency: undefined,
        locale: undefined,
      } as any,
    });
    const categoryIds: string[] = [];
    function walk(cat: any) {
      if (cat.id !== "root") categoryIds.push(cat.id);
      for (const child of cat.categories ?? []) walk(child);
    }
    walk(root);

    const params: { categoryId: string; page?: string }[] = [];
    for (const categoryId of categoryIds.slice(0, 100)) {
      const result = await commerce.shopperSearch.productSearch({
        parameters: {
          q: "",
          refine: [`cgid=${categoryId}`] as any,
          offset: 0,
          limit: 1,
        },
      });
      const total = result.total ?? 0;
      const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
      // Page 0 = no page param (default)
      params.push({ categoryId });
      for (let p = 1; p < totalPages; p++) {
        params.push({ categoryId, page: String(p) });
      }
    }
    return params;
  },
  async (ctx) => {
    const { categoryId } = ctx.params;
    const page = /^\d+$/.test(ctx.params.page ?? "") ? Number(ctx.params.page) : 0;
    const offset = page * ITEMS_PER_PAGE;
    const commerce = await createBuildCommerceClient(ctx.env);

    const [searchResult, category] = await Promise.all([
      commerce.shopperSearch.productSearch({
        parameters: {
          q: "",
          refine: [`cgid=${categoryId}`] as any,
          offset,
          limit: ITEMS_PER_PAGE,
        },
      }),
      commerce.shopperProducts.getCategory({
        parameters: {
          id: categoryId,
          levels: 2,
          currency: undefined,
          locale: undefined,
        } as any,
      }),
    ]);

    const categoryName = category.name ?? categoryId;
    const meta = ctx.use(Meta);
    meta({
      title: `${categoryName}${page > 0 ? ` - Page ${page + 1}` : ""} - Store`,
    });
    if (category.description) {
      meta({ name: "description", content: category.description });
    }

    const breadcrumbs = ctx.use(Breadcrumbs);

    breadcrumbs({
      label: categoryName,
      href: ctx.reverse(".category", { categoryId }),
    });
    /* register rendered products  */
    const productsHandle = ctx.use(ProductsHandle);
    productsHandle(searchResult?.hits?.map((hit) => hit.productId) ?? []);
    console.log("productsHandle", searchResult?.hits?.map((hit) => hit.productId) ?? []);

    const hits = searchResult.hits ?? [];
    const total = searchResult.total ?? 0;

    const categoryUrl = new URL(ctx.reverse(".category", { categoryId }), ctx.url).toString();
    const itemListElement = hits.map((hit: any, i: number) => {
      const pid = hit.productId ?? hit.representedProduct?.id;
      return {
        "@type": "ListItem",
        position: offset + i + 1,
        url: new URL(ctx.reverse(".product", { productId: pid }), ctx.url).toString(),
        name: hit.productName,
      };
    });
    meta({
      "script:ld+json": {
        "@context": "https://schema.org/",
        "@type": "CollectionPage",
        name: categoryName,
        url: categoryUrl,
        ...(category.description ? { description: category.description } : {}),
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: total,
          itemListElement,
        },
      },
    });
    ctx.set(Pagination, { total, page, perPage: ITEMS_PER_PAGE, route: ".category" });

    return (
      <div className="mx-auto max-w-7xl px-6 py-8">
        <h1 className="mb-6 text-2xl font-semibold text-slate-900">{categoryName}</h1>

        <p className="mb-6 text-sm text-slate-500">
          {total} {total === 1 ? "product" : "products"}
        </p>

        {hits.length === 0 ? (
          <p className="text-slate-500">No products found in this category.</p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {hits.map((hit: any, i: number) => {
              const productId = hit.productId ?? hit.representedProduct?.id;
              const productUrl = ctx.reverse(".product", { productId });
              return (
                <Link
                  key={hit.productId}
                  to={productUrl}
                  prefetch="viewport"
                  className="group block overflow-hidden rounded border border-slate-200 bg-white transition-shadow hover:shadow-md"
                >
                  {hit.image?.link && (
                    <div className="aspect-3/4 overflow-hidden bg-slate-100">
                      <img
                        src={hit.image.link}
                        alt={hit.productName ?? ""}
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                        loading={i < 4 ? "eager" : "lazy"}
                      />
                    </div>
                  )}
                  <div className="p-3">
                    <p className="text-sm font-medium text-slate-900">{hit.productName}</p>
                    {hit.price != null && (
                      <ProductPrice
                        productId={hit.productId}
                        price={hit.price}
                        currency={hit.currency}
                      />
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  },
  { concurrency: 8 },
);
CategoryPage.use = () => [loader(PriceLoader), layout(PaginationLayout)];
